import Phaser from 'phaser';
import { requestPasswordReset } from '../../store/auth.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { Button } from '../view/Button.js';
import { TextInputOverlay } from '../view/TextInputOverlay.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * Password reset request — the user enters their email, we ask
 * Supabase to send them a magic link, and the link routes back into
 * ResetPasswordScene where they can set a new password.
 *
 * Email-enumeration defense: Supabase's resetPasswordForEmail
 * returns success regardless of whether the email exists in the
 * system. Our UI follows that — we always show "Check your email"
 * after submit, never "no such user." Acceptable trade-off: prevents
 * attackers from probing the user database; users with typos see
 * the success state and just don't get an email (no harm).
 */
export class ForgotPasswordScene extends Phaser.Scene {
  static readonly KEY = 'ForgotPassword';

  private email = '';
  private submitting = false;

  private emailInput: TextInputOverlay | undefined = undefined;
  private submitButton: Button | undefined = undefined;
  private backButton: Button | undefined = undefined;
  private statusText!: Phaser.GameObjects.Text;

  constructor() {
    super(ForgotPasswordScene.KEY);
  }

  preload(): void {
    this.load.image('forgotPasswordLogo', '/assets/logo2.png');
  }

  init(): void {
    this.email = '';
    this.submitting = false;
    this.emailInput = undefined;
    this.submitButton = undefined;
    this.backButton = undefined;
  }

  create(): void {
    this.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        COLORS.bgDeepInt
      )
      .setOrigin(0.5);

    this.drawLogo();
    this.drawHeader();
    this.drawForm();
    this.drawSubmit();
    this.drawStatus();
    this.drawBack();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.emailInput?.destroy();
      this.submitButton?.destroy();
      this.backButton?.destroy();
    });
  }

  private drawLogo(): void {
    const logo = this.add.image(DESIGN_WIDTH / 2, 0, 'forgotPasswordLogo');
    const targetWidth = 540;
    const scale = Math.min(1, targetWidth / logo.width);
    logo.setScale(scale);
    const logoH = logo.height * scale;
    logo.setY(70 + logoH / 2);
  }

  private drawHeader(): void {
    this.add
      .text(DESIGN_WIDTH / 2, 320, 'Reset your password', {
        fontFamily: FONT_HEADER,
        fontSize: '36px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.add
      .text(
        DESIGN_WIDTH / 2,
        390,
        'Enter your email and we\'ll send you a link to set a new one.',
        {
          fontFamily: FONT_MONO,
          fontSize: '15px',
          color: COLORS.textMuted,
          align: 'center',
          wordWrap: { width: 560 },
        }
      )
      .setLetterSpacing(1)
      .setOrigin(0.5);
  }

  private drawForm(): void {
    const cx = DESIGN_WIDTH / 2;
    const fieldWidth = 520;
    const fieldHeight = 64;

    this.add
      .text(cx - fieldWidth / 2, 528, 'EMAIL', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textSecondary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);

    this.emailInput = new TextInputOverlay({
      scene: this,
      x: cx,
      y: 572,
      width: fieldWidth,
      height: fieldHeight,
      type: 'email',
      placeholder: 'you@example.com',
      autocomplete: 'email',
      maxLength: 254,
      fontSizeDesign: 26,
      onChange: (v) => {
        this.email = v;
      },
      onSubmit: () => {
        void this.handleSubmit();
      },
    });
  }

  private drawSubmit(): void {
    this.submitButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: 690,
      width: 360,
      height: 80,
      text: 'Send reset link',
      style: 'primary',
      onTap: () => {
        void this.handleSubmit();
      },
    });
  }

  private drawStatus(): void {
    this.statusText = this.add
      .text(DESIGN_WIDTH / 2, 780, '', {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color: COLORS.accentRedHex,
        fontStyle: '700',
        align: 'center',
        wordWrap: { width: 560 },
      })
      .setLetterSpacing(1)
      .setOrigin(0.5);
  }

  private drawBack(): void {
    this.backButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: 1140,
      width: 240,
      height: 64,
      text: 'Back to sign in',
      onTap: () => {
        if (this.submitting) return;
        this.scene.start('Auth');
      },
    });
  }

  private async handleSubmit(): Promise<void> {
    if (this.submitting) return;

    const email = this.email.trim();
    if (!email) {
      this.setError('Enter your email.');
      this.emailInput?.focus();
      return;
    }

    this.setSubmitting(true);
    const result = await requestPasswordReset(email);
    if (!result.ok) {
      this.setError(result.error);
      return;
    }

    this.showSuccess(email);
  }

  private setSubmitting(submitting: boolean): void {
    this.submitting = submitting;
    this.emailInput?.setDisabled(submitting);
    if (submitting) {
      this.statusText.setColor(COLORS.accentAmberHex);
      this.statusText.setText('Sending…');
    }
  }

  private setError(msg: string): void {
    this.submitting = false;
    this.emailInput?.setDisabled(false);
    this.statusText.setColor(COLORS.accentRedHex);
    this.statusText.setText(msg);
  }

  private showSuccess(email: string): void {
    // Tear down the form — we're now in a "wait for email" state and
    // re-engaging with the form would be confusing.
    this.emailInput?.destroy();
    this.emailInput = undefined;
    this.submitButton?.destroy();
    this.submitButton = undefined;
    this.backButton?.destroy();
    this.backButton = undefined;
    this.statusText.setText('');

    const cx = DESIGN_WIDTH / 2;

    this.add
      .text(cx, 580, 'Check your email', {
        fontFamily: FONT_HEADER,
        fontSize: '32px',
        color: COLORS.accentGreenHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.add
      .text(
        cx,
        650,
        `If an account exists for\n${email}\n\nyou'll receive a password-reset link shortly.`,
        {
          fontFamily: FONT_MONO,
          fontSize: '16px',
          color: COLORS.textPrimary,
          align: 'center',
          wordWrap: { width: 560 },
        }
      )
      .setLetterSpacing(1)
      .setOrigin(0.5);

    new Button(this, {
      x: cx,
      y: 880,
      width: 320,
      height: 76,
      text: 'Back to home',
      style: 'primary',
      onTap: () => this.scene.start('Home'),
    });
  }
}
